// A tab whose HTML came from one release can ask the server for that release's hashed chunks and
// be answered by a DIFFERENT release. Two deployments make it happen:
//   - App Service runs 2-6 instances and Front Door caches /assets/* between a rollout and the
//     cache purge, so a lazy chunk can 404 for the length of that window.
//   - The ACA ramp runs TWO revisions at once and splits every request independently. ACA offers
//     session affinity only in single-revision mode, so a tab cannot be pinned to the revision its
//     HTML came from; during a ramp the miss is the normal case, not a race.
//
// gemi already covers the .js half at the origin: httpProd answers a missing "*.js" with a 200 stub
// that reloads the page. This covers what that stub cannot reach — a chunk request served from the
// edge cache, a preload that fails before the stub is parsed, and the CSS/font/map requests that
// get a plain 404 — by listening for the event Vite fires when a dynamic import fails.
//
// Reloading is the whole fix: the next document comes from one release, with a matching asset set.
// The guard is that a reload must not become a loop when the failure is NOT staleness (the user is
// offline, or the asset is genuinely missing in this release). At most one reload per tab per
// RELOAD_COOLDOWN_MS, and only for a request that looks like a hashed asset.

const RELOAD_MARKER = "folio:stale-bundle-reloaded";

// The guard only has to stop a reload LOOP, which happens within seconds of the reload that caused
// it. A permanent per-tab marker would also stop the NEXT release's recovery: a tab left open across
// two releases — every release is a ramp on ACA — would reload for the first and then stay broken
// for the second. Past this window a new failure is a new staleness, not the same one again.
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

export type StaleBundleRecoveryOptions = {
  // Seams for the test; production passes nothing.
  storage?: Pick<Storage, "getItem" | "setItem">;
  reload?: () => void;
  target?: Pick<Window, "addEventListener">;
  now?: () => number;
};

// A hashed build artifact: /assets/Foo-4hK2p9Qz.js. A bare route URL or an API path is not one, and
// reloading for those would hide a real error behind a refresh.
const HASHED_ASSET = /\/assets\/.+-[A-Za-z0-9_-]{6,}\.[a-z0-9]+(\?|$)/;

function isStaleAssetUrl(raw: unknown): boolean {
  if (typeof raw !== "string" || raw.length === 0) return false;
  try {
    // Relative in the payload, absolute once resolved; both must parse for the test to be honest.
    return HASHED_ASSET.test(new URL(raw, window.location.origin).pathname);
  } catch {
    return false;
  }
}

// sessionStorage, not localStorage: the marker should die with the tab. The marker holds WHEN this
// tab last reloaded, and a throwing/absent storage (private mode, blocked site data) must not
// disable recovery — it degrades to "reload every time". Without the marker there is NO loop
// guard: when the miss is not staleness (the user is offline, or the chunk is genuinely missing
// from this release) every reload fails the same way and reloads again, indefinitely. That trade
// is deliberate — a reload loop on a page that could not render anyway, rather than a white screen
// for every blocked-storage user during a ramp. A storage-independent marker (e.g. window.name)
// would close it; tracked as a follow-up.
function reloadedRecently(
  storage: StaleBundleRecoveryOptions["storage"],
  now: number,
): boolean {
  try {
    const at = Number(storage?.getItem(RELOAD_MARKER));
    // `now >= at`: a marker from the future (the clock was set back since it was written) is not
    // a recent reload — without this, `now - at` stays negative and the cooldown never ends.
    return (
      Number.isFinite(at) &&
      at > 0 &&
      now >= at &&
      now - at < RELOAD_COOLDOWN_MS
    );
  } catch {
    return false;
  }
}

function markReloaded(
  storage: StaleBundleRecoveryOptions["storage"],
  now: number,
): void {
  try {
    storage?.setItem(RELOAD_MARKER, String(now));
  } catch {
    // Ignore: the marker is an optimization, not a correctness requirement.
  }
}

// Merely READING the `sessionStorage` global throws a SecurityError when the browser blocks site
// data, and `typeof` does not catch that. This runs at the top level of client.tsx before the app
// mounts, so a throw here would be a white screen — resolve it inside try/catch and fall back to
// no storage. Recovery still fires, but the loop guard is gone: a PERSISTENT miss (offline, a chunk
// missing from the release) then reloads on every failure, in a loop (see reloadedRecently).
function defaultStorage(): StaleBundleRecoveryOptions["storage"] {
  try {
    return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

export function installStaleBundleRecovery(
  options: StaleBundleRecoveryOptions = {},
): void {
  const storage = options.storage ?? defaultStorage();
  const target = options.target ?? window;
  const reload = options.reload ?? (() => window.location.reload());
  const clock = options.now ?? Date.now;

  target.addEventListener("vite:preloadError", (event: Event) => {
    // Vite hangs the underlying Error off the event as `payload`, and the failed URL is only in its
    // message. When no URL can be read, recover anyway: a preload error already means a chunk this
    // document needs is unavailable, which is the condition worth reloading for.
    const failedUrl = extractUrl(
      (event as Event & { payload?: { message?: string } }).payload?.message,
    );

    if (failedUrl !== undefined && !isStaleAssetUrl(failedUrl)) return;
    const now = clock();
    if (reloadedRecently(storage, now)) return;

    // Let Vite's own handler run too; preventDefault would only suppress its console error.
    markReloaded(storage, now);
    reload();
  });
}

// "Failed to fetch dynamically imported module: https://host/assets/Foo-abc123.js"
function extractUrl(message: string | undefined): string | undefined {
  if (!message) return undefined;
  const match = message.match(/https?:\/\/\S+/);
  return match?.[0];
}
